import React, { Suspense } from "react";
import Home from "./Home";
import Profile from "./Profile";
import ProtectedRoute from "./ProtectedRoute";
import PrivacyPolicy from "./PrivacyPolicy";
import RichardOsman from "./RichardOsman";
import Spa from "./Spa";
import Curate from "./Curate";
import ExploreDispatcher from "./ExploreDispatcher";
import TrackDispatcher from "./TrackDispatcher";
import Stats from "./Stats";
import RootLayout from "./RootLayout";
import NotFoundPage from "./NotFoundPage";
import TrackRedirect from "../components/TrackPageRedirect";
import Import from "../features/import/Import";
import ImportLanding from "../features/import/ImportLanding";
import PollPage from "./PollPage";
import CurateRedirect from "../components/CuratePageRedirect";
import ProfileRedirect from "../components/ProfilePageRedirect";
import SettingsProfile from "../features/settings/SettingsProfile";
import SettingsPreferences from "../features/settings/SettingsPreferences";
import SettingsServices from "../features/settings/SettingsServices";
import Onboarding from "../features/settings/Onboarding";
import WhatsNew from "../features/whats-new/WhatsNew";
import ContentDetailRoute from "../features/detail/ContentDetailRoute";
import CollectionDetailRoute from "../features/detail/CollectionDetailRoute";

// Admin-only and chart-heavy: lazily loaded so its weight stays out of the
// bundle every other user downloads.
const AdminMetrics = React.lazy(() => import("../features/admin/AdminMetrics"));
const AdminCovers = React.lazy(() => import("../features/admin/AdminCovers"));

// The collection/content detail dialogs, shared verbatim between the full page
// tree (a cold load renders them inside the app shell) and the modal tree (an
// in-app open renders just the dialog over whatever's already mounted) — see
// AppRoutes.js for how the two trees get chosen between.
const detailRouteChildren = [
  { path: "list/:listId/:slug?", element: <CollectionDetailRoute /> },
  {
    path: "movie/:contentId/:slug?",
    element: <ContentDetailRoute contentType="movie" />,
  },
  {
    path: "tv/:contentId/:slug?",
    element: <ContentDetailRoute contentType="tvshow" />,
  },
  {
    path: "book/:contentId/:slug?",
    element: <ContentDetailRoute contentType="book" />,
  },
];

export const modalRoutes = detailRouteChildren.map(({ path, element }) => ({
  path: `/${path}`,
  element,
}));

export const routes = [
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { index: true, element: <Home /> },
      { path: "privacy", element: <PrivacyPolicy /> },
      // URL-only page, not linked from any nav — shared directly (e.g. via QR code).
      { path: "message-for-richard", element: <RichardOsman /> },
      {
        path: "poll",
        element: <PollPage />,
      },
      {
        element: <Spa />,
        children: [
          // What's New feed
          { path: "whats-new", element: <WhatsNew /> },

          // Explore — all /explore/* paths flow through the dispatcher
          { path: "explore", element: <ExploreDispatcher /> },
          { path: "explore/:segment", element: <ExploreDispatcher /> },
          { path: "explore/:segment/:contentType", element: <ExploreDispatcher /> },

          { path: "track", element: <TrackRedirect /> },
          { path: "curate", element: <CurateRedirect /> },
          // Your own page, for links that can't name you (e.g. release notes)
          { path: "dashboard", element: <ProfileRedirect /> },
          { path: "profile", element: <ProfileRedirect tab="profile" /> },

          // Username-based app pages
          { path: "u/:username", element: <Profile /> },
          { path: "u/:username/profile", element: <Profile tab="profile" /> },
          {
            path: "track/u/:username/:contentType/:status",
            element: <TrackDispatcher />,
          },
          // All content types: no content type segment
          { path: "track/u/:username/:status", element: <TrackDispatcher /> },
          { path: "track/u/:username", element: <TrackRedirect /> },
          { path: "stats/u/:username", element: <Stats /> },

          { path: "curate/u/:username", element: <Curate /> },
          { path: "curate/u/:username/saved", element: <Curate /> },

          // Private pages
          { path: "import", element: <ImportLanding /> },
          {
            path: "import/:source",
            element: (
              <ProtectedRoute>
                <Import />
              </ProtectedRoute>
            ),
          },
          {
            path: "settings/profile",
            element: (
              <ProtectedRoute>
                <SettingsProfile />
              </ProtectedRoute>
            ),
          },
          {
            path: "settings/preferences",
            element: (
              <ProtectedRoute>
                <SettingsPreferences />
              </ProtectedRoute>
            ),
          },
          {
            path: "settings/services",
            element: (
              <ProtectedRoute>
                <SettingsServices />
              </ProtectedRoute>
            ),
          },
          {
            // Admin-gated server-side; a non-admin who guesses the URL gets the
            // 403 rendered as a message rather than a client-side redirect,
            // which would require shipping the admin id list to the browser.
            path: "admin",
            element: (
              <ProtectedRoute>
                <Suspense fallback={null}>
                  <AdminMetrics />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            // Same server-side admin gate as above
            path: "admin/covers",
            element: (
              <ProtectedRoute>
                <Suspense fallback={null}>
                  <AdminCovers />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: "onboarding",
            element: (
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            ),
          },

          ...detailRouteChildren,
        ],
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];
